import { Mic2, Megaphone, Disc3, CalendarClock } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Kinetic";
import EditorialMarquee from "@/components/Marquee";
import LabelExtras from "@/components/LabelExtras";

const LOGO_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_soundwave-shop-10/artifacts/ol4frpvy_03915719-7346-49A6-A7AA-BE0F4907F16D.PNG";

const SERVICES = [
  { icon: Mic2, title: "PRODUCTION", body: "Beats and records built from Southern soil — raw, storytelling-first, mixed to rattle trunks." },
  { icon: Megaphone, title: "MARKETING", body: "Rollouts, visuals and campaigns that make noise regionally first, nationally next." },
  { icon: Disc3, title: "DISTRIBUTION", body: "Every release delivered to every platform, with the paperwork handled so artists stay creating." },
  { icon: CalendarClock, title: "EVENTS", body: "Shows, showcases and tours — real stages for local talent, promoted the Southern Smoke way." },
];

const CHAPTERS = [
  {
    num: "01",
    title: "THE MISSION",
    body: "Southern Smoke Music Group is an independent music label and entertainment company focused on promoting and developing artists within Southern hip-hop and rap. The label exists to nurture local talent and give artists a platform for styles rooted in Southern culture and musical tradition.",
  },
  {
    num: "02",
    title: "THE SOUND",
    body: "Authenticity over everything. Storytelling, raw lyricism, and beats that resonate with real fans of Southern rap — music built to be felt in the chest before it's analyzed in the head.",
  },
  {
    num: "03",
    title: "THE COMMUNITY",
    body: "A network of producers, artists and industry professionals moving as one. Southern Smoke is a community that encourages creativity and innovation — and a significant force in the genre's ongoing evolution.",
  },
];

export default function Label() {
  return (
    <main data-testid="label-page" className="pb-40 pt-36">
      <div className="mx-auto max-w-7xl px-6">
        <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">INDEPENDENT. SOUTHERN. UNMATCHED.</p>
        <h1 data-testid="label-title" className="font-anton leading-[0.9]">
          <MaskedLine className="text-6xl text-white md:text-8xl" delay={0.1}>THE</MaskedLine>
          <MaskedLine className="text-outline glow-blue text-6xl md:text-8xl" delay={0.25}>LABEL</MaskedLine>
        </h1>
      </div>

      <Reveal className="mx-auto mt-16 max-w-7xl px-6">
        <div
          data-testid="label-logo-frame"
          className="relative mx-auto max-w-2xl border border-primary/40 bg-black p-8 md:p-14"
        >
          <div className="absolute -left-px -top-px h-8 w-8 border-l-2 border-t-2 border-primary" />
          <div className="absolute -bottom-px -right-px h-8 w-8 border-b-2 border-r-2 border-primary" />
          <div className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/20 blur-[110px]" />
          <img
            src={LOGO_IMG}
            alt="Southern Smoke Music Group official logo"
            data-testid="label-logo-image"
            className="relative mx-auto w-full max-w-md"
          />
          <p className="mt-8 text-center font-mono text-[10px] tracking-[0.35em] text-white/50">
            EST. IN THE SOUTH — RUN BY THE CULTURE
          </p>
        </div>
      </Reveal>

      <div className="mx-auto mt-24 grid max-w-7xl gap-20 px-6 lg:grid-cols-[1fr_1.2fr]">
        <Reveal className="lg:sticky lg:top-32 lg:self-start">
          <p className="font-mono text-xs tracking-[0.4em] text-primary">WHO WE ARE</p>
          <h2 className="mt-4 font-anton text-4xl leading-tight text-white md:text-6xl">
            MORE THAN A LABEL.<br />
            <span className="text-primary">A MOVEMENT.</span>
          </h2>
          <p className="mt-6 leading-relaxed text-white/65">
            From the block to the main stage, Southern Smoke handles the business so artists can handle the art.
            We build careers regionally first — then take them national.
          </p>
        </Reveal>

        <div data-testid="label-chapters" className="space-y-20">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.05}>
              <div data-testid={`label-chapter-${c.num}`} className="border-l-2 border-primary/40 pl-8">
                <span className="font-mono text-sm tracking-[0.4em] text-primary">{c.num}</span>
                <h3 className="mt-3 font-anton text-4xl text-white md:text-5xl">{c.title}</h3>
                <p className="mt-5 leading-relaxed text-white/65">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <EditorialMarquee
        className="mt-32"
        speed={40}
        items={["SOUTHERN SMOKE MUSIC GROUP", "INDEPENDENT", "SOUTHERN HIP-HOP", "ARTIST FIRST"]}
      />

      <section data-testid="label-services" className="mx-auto mt-24 max-w-7xl px-6">
        <Reveal>
          <p className="mb-3 font-mono text-xs tracking-[0.4em] text-primary">04 / WHAT WE DO</p>
          <h2 className="font-anton text-5xl text-white md:text-7xl">FULL SERVICE</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div
                data-testid={`label-service-${s.title.toLowerCase()}`}
                className="group h-full border border-white/10 bg-card p-8 transition-colors duration-300 hover:border-primary/50"
              >
                <s.icon size={28} className="text-primary transition-transform duration-300 group-hover:-translate-y-1" />
                <h3 className="mt-6 font-oswald text-xl font-bold tracking-wider text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <LabelExtras />
    </main>
  );
}
